import { useContext, useState } from "react";

import { queryClient } from "App";
import { Button } from "@/components/ui/button";
import CustomTableImportService from "services/CustomTableImport";
import { useImportJob } from "hooks/useImportJob";
import { invalidateCustomTablesQuery } from "hooks/useCustomTablesAdminGrid";
import {
  customTableRowsKey,
  customTableSchemaKey,
} from "routes/custom_tables/grid/queryKeys";

import { ImportTargetContext } from "../ImportTargetContext";
import StatsStrip from "../StatsStrip";
import { formatError } from "../../helpers";

const GENERIC_KEY = "_";

const extractGenericMessage = err => {
  const errors = err?.response?.data?.errors;
  if (errors && !Array.isArray(errors) && typeof errors === "object") {
    const value = errors[GENERIC_KEY];
    if (typeof value === "string" && value) return value;
    if (Array.isArray(value)) {
      const first = value.find(v => typeof v === "string" && v);
      if (first) return first;
    }
  } else if (Array.isArray(errors)) {
    const generic = errors.find(e => !e?.field || e.field === GENERIC_KEY);
    if (generic?.message) return generic.message;
  }
  return null;
};

// Conflict responses (SLUG_CONFLICT, IMPORT_ALREADY_*) carry a singular
// `message`, not the plural `errors` object extractGenericMessage reads.
// Surface it before falling back to formatError, which would otherwise show
// the raw `error` machine code.
const formatImportError = (err, fallback) =>
  extractGenericMessage(err) ??
  err?.response?.data?.message ??
  formatError(err, fallback);

const buildConfirmPayload = (
  kind,
  mode,
  targetSlug,
  { targetDisplayName, targetSingularName, targetIconName, targetDescription },
  mapping
) => {
  const columns = (mapping ?? []).map((row, index) => ({
    csv_column_name: row.csv_column_name,
    csv_column_index: row.csv_column_index ?? index,
    include: row.include,
    display_name: row.display_name,
    field_id: row.field_id,
    type: row.type,
    is_required: row.is_required,
    is_unique: row.is_unique,
    options: row.options ?? null,
    allow_choice_additions: row.allow_choice_additions ?? false,
  }));
  if (mode === "append") {
    return {
      kind,
      mode: "append",
      target_slug: targetSlug,
      columns,
    };
  }
  return {
    kind,
    mode: "new_table",
    target_display_name: targetDisplayName,
    target_singular_name: targetSingularName || null,
    target_icon_name: targetIconName || null,
    target_description: targetDescription || null,
    columns,
  };
};

const Step4Run = ({ onBack, onCancel, onDone }) => {
  const {
    kind,
    mode,
    targetSlug,
    parseResult,
    mapping,
    dataJobId,
    targetDisplayName,
    targetSingularName,
    targetIconName,
    targetDescription,
  } = useContext(ImportTargetContext);
  const [hasConfirmed, setHasConfirmed] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const { progress, isComplete, isFailed, isAborted, isStalled, job } =
    useImportJob(hasConfirmed ? dataJobId : null);
  const isImportRunning =
    hasConfirmed && dataJobId != null && !isComplete && !isFailed;

  const includedRows = (mapping ?? []).filter(r => r.include);
  const totalRows = parseResult?.row_count ?? 0;

  // Upsert detection: any included mapping row keyed off the synthetic
  // ``id`` system column. When set, we count filled vs blank ids in the
  // inferred sample to give the operator concrete preview numbers.
  const idMapping = (mapping ?? []).find(r => r.include && r.field_id === "id");
  const isUpsert = Boolean(idMapping);

  const upsertPreview = (() => {
    if (!isUpsert) return null;
    const samples = parseResult?.sample_rows ?? [];
    if (!samples.length) return null;
    let filled = 0;
    let blank = 0;
    for (const sample of samples) {
      const value = sample?.[idMapping.csv_column_name];
      if (value == null || String(value).trim() === "") blank += 1;
      else filled += 1;
    }
    if (filled === 0 && blank === 0) return null;
    return { filled, blank };
  })();

  const handleConfirm = async () => {
    setSubmitError(null);
    try {
      await CustomTableImportService.confirm(
        dataJobId,
        buildConfirmPayload(
          kind,
          mode,
          targetSlug,
          {
            targetDisplayName,
            targetSingularName,
            targetIconName,
            targetDescription,
          },
          mapping
        )
      );
      setHasConfirmed(true);
    } catch (err) {
      setSubmitError(
        formatImportError(
          err,
          "Something went wrong while starting the import."
        )
      );
    }
  };

  const handleRetry = () => {
    setHasConfirmed(false);
  };

  // New-table mode only learns the final slug from the job info (the
  // confirm endpoint derives it server-side); append mode knows it from
  // the table picker.
  const handleDone = () => {
    const slug = progress?.target_slug ?? targetSlug ?? null;
    if (slug) {
      queryClient.invalidateQueries({ queryKey: customTableSchemaKey(slug) });
      queryClient.invalidateQueries({ queryKey: customTableRowsKey(slug) });
    }
    if (mode === "new_table") {
      invalidateCustomTablesQuery();
    }
    onDone?.(slug);
  };

  const handleCancelRun = async () => {
    if (isImportRunning) {
      try {
        await CustomTableImportService.abortJob(dataJobId);
      } catch {
        // Worker may have already terminated; closing the modal is still
        // the right outcome.
      }
    }
    onCancel?.();
  };

  const handleArtifactDownload = async kindKey => {
    try {
      const result = await CustomTableImportService.getArtifactUrl(
        dataJobId,
        kindKey
      );
      if (result?.url) {
        window.open(result.url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      setSubmitError(
        formatImportError(err, "We couldn't generate that download link.")
      );
    }
  };

  if (!hasConfirmed) {
    const isAppend = mode === "append";
    // When the sample reliably contains a non-empty id and at least one
    // blank id we scale the totals to the sample's fill ratio so the
    // copy reflects the upsert split. The exact server-side counts
    // surface in the post-run progress screen.
    let upsertLead = null;
    let upsertCta = null;
    if (isAppend && isUpsert && upsertPreview && upsertPreview.filled > 0) {
      const sampleTotal = upsertPreview.filled + upsertPreview.blank;
      const updateFraction = upsertPreview.filled / sampleTotal;
      const estimatedUpdates = Math.round(totalRows * updateFraction);
      const estimatedInserts = totalRows - estimatedUpdates;
      upsertLead = (
        <>
          About to append {estimatedInserts} new rows to{" "}
          <strong>{targetDisplayName ?? targetSlug}</strong> and update{" "}
          {estimatedUpdates} existing rows.
        </>
      );
      upsertCta = `Import ${estimatedInserts} new + update ${estimatedUpdates} existing rows`;
    }
    return (
      <div className="tw:flex tw:flex-col tw:gap-4 tw:p-6">
        <h2 className="tw:font-heading tw:text-foreground tw:m-0 tw:text-2xl">
          Confirm and import
        </h2>
        <p className="tw:text-muted-foreground tw:m-0">
          {upsertLead ??
            (isAppend ? (
              <>
                About to append {totalRows} rows to{" "}
                <strong>{targetDisplayName ?? targetSlug}</strong>.
              </>
            ) : targetDisplayName ? (
              <>
                About to create <strong>{targetDisplayName}</strong> with{" "}
                {includedRows.length} columns and import {totalRows} rows.
              </>
            ) : (
              <>About to import {totalRows} rows.</>
            ))}
        </p>

        {submitError && (
          <div className="tw:px-4 tw:py-3 tw:bg-warning/10 tw:border tw:border-warning/20 tw:rounded-md tw:text-warning tw:dark:bg-warning/20 tw:text-sm">
            {submitError}
          </div>
        )}

        <div className="tw:flex tw:justify-between tw:gap-3 tw:pt-3 tw:border-t tw:border-border">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button onClick={handleConfirm}>
            {upsertCta ??
              (isAppend
                ? `Append ${totalRows} rows`
                : `Create & Import ${totalRows} rows`)}
          </Button>
        </div>
      </div>
    );
  }

  if (isFailed) {
    const title = isAborted ? "Import canceled" : "Import failed";
    const fallback = isAborted
      ? "The import was canceled. Partial results may be available below."
      : "The import failed.";
    const errorMsg = job?.info?.error_msg ?? fallback;
    const artifacts = progress?.artifacts ?? {};
    const hasSkipped = Boolean(artifacts.skipped_rows_key);
    const hasErrored = Boolean(artifacts.errored_rows_key);
    return (
      <div className="tw:flex tw:flex-col tw:gap-4 tw:p-6">
        <h2 className="tw:font-heading tw:text-foreground tw:m-0 tw:text-2xl">
          {title}
        </h2>
        <div className="tw:px-4 tw:py-3 tw:bg-warning/10 tw:border tw:border-warning/20 tw:rounded-md tw:text-warning tw:dark:bg-warning/20 tw:text-sm">
          {errorMsg}
        </div>
        {(hasSkipped || hasErrored) && (
          <div className="tw:flex tw:gap-4">
            {hasSkipped && (
              <button
                type="button"
                onClick={() => handleArtifactDownload("skipped_rows")}
                className="tw:text-[var(--color-blue)]! tw:underline tw:text-sm"
              >
                Download skip report
              </button>
            )}
            {hasErrored && (
              <button
                type="button"
                onClick={() => handleArtifactDownload("errored_rows")}
                className="tw:text-[var(--color-blue)]! tw:underline tw:text-sm"
              >
                Download error report
              </button>
            )}
          </div>
        )}
        <div className="tw:flex tw:justify-between tw:gap-3 tw:pt-3 tw:border-t tw:border-border">
          <Button variant="outline" onClick={handleRetry}>
            Back
          </Button>
          <Button onClick={onCancel}>Close</Button>
        </div>
      </div>
    );
  }

  if (isComplete) {
    const artifacts = progress?.artifacts ?? {};
    const importedCount = progress?.imported ?? 0;
    const updatedCount = progress?.updated_count ?? 0;
    const skippedCount = progress?.skipped ?? 0;
    const erroredCount = progress?.errored ?? 0;
    const idNotFoundCount = progress?.id_not_found_count ?? 0;
    // Render a download CTA only when both the count is non-zero AND the
    // worker actually uploaded the artifact. Gating on the count keeps
    // empty-artifact links from showing; gating on the key keeps the
    // CTA from 404-ing if uploads were skipped (e.g., aborted runs).
    const showSkippedDownload =
      skippedCount > 0 && Boolean(artifacts.skipped_rows_key);
    const showErroredDownload =
      (erroredCount > 0 || idNotFoundCount > 0) &&
      Boolean(artifacts.errored_rows_key);
    // All-rows-dropped: a "successful" job that wrote nothing. Upsert
    // runs still count when ``updated_count`` is non-zero — only flag
    // when both insert and update buckets are empty and at least one
    // row was diverted to skipped/errored.
    const wroteNothing =
      importedCount === 0 &&
      (!isUpsert || updatedCount === 0) &&
      skippedCount + erroredCount > 0;
    const title = wroteNothing ? "Nothing was imported" : "Import complete";
    return (
      <div className="tw:flex tw:flex-col tw:gap-4 tw:p-6">
        <h2 className="tw:font-heading tw:text-foreground tw:m-0 tw:text-2xl">
          {title}
        </h2>
        {wroteNothing && (
          <div className="tw:px-4 tw:py-3 tw:bg-warning/10 tw:border tw:border-warning/20 tw:rounded-md tw:text-warning tw:dark:bg-warning/20 tw:text-sm">
            Every row was diverted to the skip or error report — no data made it
            into the table. Download the report
            {showSkippedDownload && showErroredDownload ? "s" : ""} below to see
            why each row was rejected.
          </div>
        )}
        <StatsStrip
          imported={importedCount}
          updated={updatedCount}
          idNotFound={idNotFoundCount}
          skipped={skippedCount}
          errored={erroredCount}
          isUpsert={isUpsert}
        />
        {submitError && (
          <div className="tw:px-4 tw:py-3 tw:bg-warning/10 tw:border tw:border-warning/20 tw:rounded-md tw:text-warning tw:dark:bg-warning/20 tw:text-sm">
            {submitError}
          </div>
        )}
        {(showSkippedDownload || showErroredDownload) && (
          <div className="tw:flex tw:gap-4">
            {showSkippedDownload && (
              <button
                type="button"
                onClick={() => handleArtifactDownload("skipped_rows")}
                className="tw:text-[var(--color-blue)]! tw:underline tw:text-sm"
              >
                Download skipped rows
              </button>
            )}
            {showErroredDownload && (
              <button
                type="button"
                onClick={() => handleArtifactDownload("errored_rows")}
                className="tw:text-[var(--color-blue)]! tw:underline tw:text-sm"
              >
                Download errored rows
              </button>
            )}
          </div>
        )}
        <div className="tw:flex tw:justify-between tw:gap-3 tw:pt-3 tw:border-t tw:border-border">
          {wroteNothing && (
            <Button variant="outline" onClick={handleRetry}>
              Retry
            </Button>
          )}
          <Button onClick={handleDone}>Done</Button>
        </div>
      </div>
    );
  }

  const percent =
    progress?.total_rows && progress.total_rows > 0
      ? Math.round(((progress.current_row ?? 0) / progress.total_rows) * 100)
      : 0;

  return (
    <div className="tw:flex tw:flex-col tw:gap-4 tw:p-6">
      <h2 className="tw:font-heading tw:text-foreground tw:m-0 tw:text-2xl">
        Importing…
      </h2>

      {isStalled && (
        <div className="tw:px-4 tw:py-3 tw:bg-warning/10 tw:border tw:border-warning/20 tw:rounded-md tw:text-warning tw:dark:bg-warning/20 tw:text-sm">
          The import looks stalled — we haven't seen progress in a few minutes.
          The connection may have dropped.
        </div>
      )}

      <div
        className="tw:w-full tw:h-2 tw:bg-accent tw:rounded-sm tw:overflow-hidden"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="tw:h-full tw:bg-primary tw:transition-[width] tw:duration-200"
          style={{ width: `${percent}%` }}
        />
      </div>

      <StatsStrip
        imported={progress?.imported ?? 0}
        updated={progress?.updated_count ?? 0}
        idNotFound={progress?.id_not_found_count ?? 0}
        skipped={progress?.skipped ?? 0}
        errored={progress?.errored ?? 0}
        isUpsert={isUpsert}
      />

      <div className="tw:flex tw:justify-between tw:gap-3 tw:pt-3 tw:border-t tw:border-border">
        <Button variant="outline" onClick={handleCancelRun}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default Step4Run;
